/**
 * capabilities.ts
 * Capability matching for UI visibility (operator-tabs-by-role). Mirrors
 * matchCapability in packages/kernel/src/http/capability.guard.ts and
 * apps/frontend/src/lib/capabilities.ts: this app cannot import either, so
 * the glob semantics are copied and pinned by the same test vectors
 * (lib/__tests__/capabilities.spec.ts).
 *
 *   '*'        — matches any non-empty required capability
 *   '<ns>:*'   — matches any capability starting with '<ns>:'
 *   '<exact>'  — exact string equality
 *   empty caps — always false (fail-closed)
 *
 * This only decides what the UI shows. The backend guard on every route is
 * still the authority.
 */
import { computed, type ComputedRef } from 'vue';
import { currentClaims } from './session.js';

export function hasCapability(held: readonly string[], required: string): boolean {
  if (!required || held.length === 0) return false;
  for (const cap of held) {
    if (cap === '*') return true;
    if (cap === required) return true;
    if (cap.length > 2 && cap.endsWith(':*') && required.startsWith(cap.slice(0, -1))) return true;
  }
  return false;
}

export function hasAnyCapability(held: readonly string[], required: readonly string[]): boolean {
  return required.some((req) => hasCapability(held, req));
}

/** The signed-in user's capabilities (empty when signed out or platform-scoped). */
export const currentCapabilities: ComputedRef<readonly string[]> = computed(() => {
  const claims = currentClaims.value;
  return claims?.scope === 'tenant' ? claims.capabilities : [];
});
